import { QuoteDrawer } from '@/components/Quotes/QuoteDrawer';
import { currencyFormatter } from '@/helpers/currencyFormatter';
import { useGetDrawerMarginStyles } from '@/hooks/useGetDrawerMarginStyles';
import { useParentDivLayoutStyles } from '@/hooks/useParentDivLayoutStyles';
import usePermissions from '@/hooks/usePermissions';
import { useGetAccountQuery } from '@/services/accounts';
import { useGetContractsQuery, useSendContractV2Mutation, useVoidContractMutation } from '@/services/contract';
import { useFetchGeneratorAccountPricingMutation } from '@/services/pricing';
import { useCreateQuoteMutation, useGetQuotesQuery, useVoidQuoteMutation } from '@/services/quotes';
import {
  useCreateSystemDesignMutation,
  useGetCurrentDesignQuery,
  useLazyGetCurrentDesignQuery,
} from '@/services/systemDesign';
import { Alert, Box, Button, Modal, Spinner, toast, useOpenClose } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useMemo, useRef, useState } from 'react';
import { FieldErrors, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import {
  ContractStatus,
  EquipmentPricingDetails,
  // Site prep, accessories, and discounts are temporarily commented out for the generator program; may return.
  // GeneratorAccessoryItem,
  // GeneratorDiscountItem,
  GeneratorEquipmentItem,
  GeneratorEquipmentType,
  GeneratorPreliminaryEquipmentItem,
  // GeneratorSitePrepItem,
  GeneratorSystem,
  ProgramType,
  QuoteStatus,
  UserPermissions,
} from 'types';

import { useIsOrgReadOnly } from '../../auth/claimsUtils';
import RequirePermissions from '../../auth/requirePermissions';
import { MainContainer } from '../../MainContainer';
import { calculateGeneratorSystemDesignTotalCost } from '../../Quotes/GeneratorQuoteCalculator.utils';

import { SystemSection } from './SystemSection';

const formatSystemData = (values: any, isPreliminary: boolean) => {
  return values.systems.map((system: any) => {
    const equipmentItems = isPreliminary
      ? system.equipment.items.map((item: GeneratorPreliminaryEquipmentItem) => ({
          type: item.type,
          quantity: 1,
          fuelType: item.fuelType,
          ratedKw: item.ratedKw,
        }))
      : system.equipment.items.map((item: GeneratorEquipmentItem) => ({
          type: item.type,
          manufacturer: item.manufacturer,
          model: item.model,
          quantity: 1,
          serialNumbers: item.serialNumbers,
          fuelType: item.fuelType,
          ratedKw: item.ratedKw,
          runningKw: item.runningKw,
          lpPowerRating: item.lpPowerRating,
          gasPowerRating: item.gasPowerRating,
        }));

    return {
      name: system.name,
      equipment: {
        totalCost: system.equipment.totalCost,
        items: equipmentItems,
      },
      // Site prep, accessories, and discounts are temporarily commented out for the generator program; may return.
      // sitePrep: system?.sitePrep
      //   ? {
      //       totalCost: system.sitePrep.totalCost,
      //       items: system.sitePrep.items.map((item: GeneratorSitePrepItem) => ({
      //         name: item.name,
      //       })),
      //     }
      //   : undefined,
      // accessories: system?.accessories
      //   ? {
      //       totalCost: system.accessories.totalCost,
      //       items: system.accessories.items.map((item: GeneratorAccessoryItem) => ({
      //         name: item.name,
      //       })),
      //     }
      //   : undefined,
      // discounts: system?.discounts
      //   ? {
      //       totalCost: system.discounts.totalCost,
      //       items: system.discounts.items.map((item: GeneratorDiscountItem) => ({
      //         name: item.name,
      //       })),
      //     }
      //   : undefined,
    };
  });
};

interface GeneratorSystemFormData extends Omit<GeneratorSystem, 'equipment'> {
  equipment: {
    items: (GeneratorEquipmentItem | GeneratorPreliminaryEquipmentItem)[];
    totalCost?: number | string;
  };
}

interface GeneratorFormValues {
  systems: GeneratorSystemFormData[];
}

const validateGeneratorForm = (values: GeneratorFormValues, isPreliminary: boolean): Record<string, any> => {
  const errors: Record<string, any> = {};

  values.systems.forEach((system, systemIndex) => {
    // Equipment total cost required (can be 0 but must be present)
    if (system.equipment?.totalCost == null || system.equipment.totalCost === '') {
      if (!errors.systems) errors.systems = [];
      if (!errors.systems[systemIndex]) errors.systems[systemIndex] = {};
      if (!errors.systems[systemIndex].equipment) errors.systems[systemIndex].equipment = {};
      errors.systems[systemIndex].equipment.totalCost = {
        type: 'required',
        message: 'Equipment total cost is required',
      };
    }

    const items = system.equipment?.items || [];

    if (items.length === 0) {
      if (!errors.systems) errors.systems = [];
      if (!errors.systems[systemIndex]) errors.systems[systemIndex] = {};
      errors.systems[systemIndex].equipment = {
        items: { type: 'required', message: 'At least one equipment item is required' },
      };
      return;
    }

    items.forEach((item: any, itemIndex: number) => {
      const itemErrors: any = {};
      const typeLabel = `Generator ${itemIndex + 1}`;

      if (!item.fuelType) {
        itemErrors.fuelType = { type: 'required', message: `Fuel type is required for ${typeLabel}` };
      }

      if (!isPreliminary) {
        if (!item.manufacturer) {
          itemErrors.manufacturer = { type: 'required', message: `Manufacturer is required for ${typeLabel}` };
        }
        if (!item.model) {
          itemErrors.model = { type: 'required', message: `Model is required for ${typeLabel}` };
        }
      }

      if (Object.keys(itemErrors).length > 0) {
        if (!errors.systems) errors.systems = [];
        if (!errors.systems[systemIndex]) errors.systems[systemIndex] = {};
        if (!errors.systems[systemIndex].equipment) errors.systems[systemIndex].equipment = {};
        if (!errors.systems[systemIndex].equipment.items) errors.systems[systemIndex].equipment.items = [];
        errors.systems[systemIndex].equipment.items[itemIndex] = itemErrors;
      }
    });
  });

  return errors;
};

const flattenErrors = (errors: FieldErrors<GeneratorFormValues>, values: GeneratorFormValues): string[] => {
  const messages: string[] = [];
  const systems = errors.systems as any[] | undefined;
  if (!systems) return messages;

  systems.forEach((systemErrors: any, systemIndex: number) => {
    if (!systemErrors) return;
    const systemName = values.systems[systemIndex]?.name || `System ${systemIndex + 1}`;
    const prefix = systemName;

    if (systemErrors.equipment) {
      if (systemErrors.equipment.totalCost?.message) {
        messages.push(`${prefix} Equipment: ${systemErrors.equipment.totalCost.message}`);
      }
      if (systemErrors.equipment.items?.message) {
        messages.push(`${prefix} Equipment: ${systemErrors.equipment.items.message}`);
      } else if (Array.isArray(systemErrors.equipment.items)) {
        systemErrors.equipment.items.forEach((itemErrors: any, itemIndex: number) => {
          if (!itemErrors) return;
          const typeLabel = `Generator ${itemIndex + 1}`;
          Object.values(itemErrors).forEach((err: any) => {
            const msg = err?.message ?? err;
            if (typeof msg === 'string') {
              messages.push(`${prefix} Equipment — ${typeLabel}: ${msg}`);
            }
          });
        });
      }
    }
  });

  return messages;
};

export const GeneratorSystemDesign = () => {
  const targetRef = useRef<HTMLElement | null>(null);
  const navigate = useNavigate();
  const { isOpen: isDrawerOpen, handleOpen: openDrawer, handleClose: closeDrawer } = useOpenClose();

  const parentStyles = useParentDivLayoutStyles(targetRef);
  const { id } = useParams<{ id: any }>();
  const [createQuote] = useCreateQuoteMutation();
  const drawerMarginStyles = useGetDrawerMarginStyles(parentStyles, isDrawerOpen);

  const { data: systemDesign } = useGetCurrentDesignQuery(
    {
      id,
      programType: ProgramType.generator,
    },
    { skip: !id },
  );
  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery(id, { skip: !id });
  const programType = account?.programType;
  const { data: quotes, isLoading: isQuotesLoading } = useGetQuotesQuery(
    { id, programType },
    { skip: !programType || !id },
  );
  const [voidQuote, { isLoading: isVoidingQuote }] = useVoidQuoteMutation({
    fixedCacheKey: 'useVoidQuoteMutationKey',
  });
  const { data: contracts, isLoading: areContractsLoading } = useGetContractsQuery(id, { skip: !id });
  const [fetchPricing, { data: pricingData = [], isLoading: isPricingLoading, status: pricingStatus }] =
    useFetchGeneratorAccountPricingMutation();

  const isLoading = isAccountLoading || isQuotesLoading || areContractsLoading;
  const [createSystemDesign, { isLoading: isCreatingSystemDesign }] = useCreateSystemDesignMutation();
  const [getCurrentSystemDesign, { data: createdSystemDesign }] = useLazyGetCurrentDesignQuery();
  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();
  const {
    isOpen: isQuoteCreationModalOpen,
    handleOpen: openQuoteCreationModal,
    handleClose: closeQuoteCreationModal,
  } = useOpenClose();
  const { comfortPlanDealerConditions } = useFlags();
  const userPermissions = usePermissions();
  const [sendContract, { isLoading: isSendingContract }] = useSendContractV2Mutation({
    fixedCacheKey: 'useSendContractV2MutationKey',
  });
  const [formValues, setFormValues] = useState<any>({});
  const [createdQuote, setCreatedQuote] = useState<any>(null);
  const allowsAction = useIsOrgReadOnly();
  const isPreliminary = false;

  const activeContract = useMemo(() => {
    return contracts?.find(
      (contract: any) => ![ContractStatus.voided, ContractStatus.rejected].includes(contract.status),
    );
  }, [contracts]);

  const hasSignedContract = useMemo(() => {
    const signedStatuses = new Set([
      ContractStatus.signed,
      ContractStatus.partiallySigned,
      ContractStatus.approved,
      ContractStatus.disclosureSigned,
    ]);
    return !!contracts?.some((contract: any) => signedStatuses.has(contract.status));
  }, [contracts]);

  const [voidContract, { isLoading: isVoidingActiveContract }] = useVoidContractMutation({
    fixedCacheKey: 'useVoidContractMutationKey',
  });

  const systemDesignCost = useMemo(() => {
    if (!createdSystemDesign && !systemDesign) {
      return 0;
    }
    return calculateGeneratorSystemDesignTotalCost(createdSystemDesign || systemDesign);
  }, [createdSystemDesign, systemDesign]);

  const activeQuote = quotes?.find((quote: any) => quote.status === QuoteStatus.active);

  const calculateTotalCost = useCallback((values: { systems: GeneratorSystem[] }) => {
    const totalCost = calculateGeneratorSystemDesignTotalCost(values as any) || 0;
    return totalCost;
  }, []);

  const toggleDrawer = useCallback(() => {
    if (isDrawerOpen) {
      closeDrawer();
    } else {
      openDrawer();
    }
  }, [isDrawerOpen, closeDrawer, openDrawer]);

  const initialValues = useMemo<GeneratorFormValues>(() => {
    if (!systemDesign || !systemDesign?.systems?.length) {
      const initialCount =
        account?.propertyDetails?.numberOfSystems ?? account?.propertyDetails?.systemsBeingReplaced ?? 1;
      const systemsToCreate = Array.from({ length: initialCount });
      return {
        systems: systemsToCreate.map((_, index: number) => ({
          name: `System ${index + 1}`,
          equipment: {
            totalCost: 0,
            items: [
              {
                type: GeneratorEquipmentType.generator,
                quantity: 1,
              },
            ],
          },
          // Site prep, accessories, and discounts are temporarily commented out for the generator program; may return.
          // sitePrep: {
          //   totalCost: 0,
          //   items: [],
          // },
          // accessories: {
          //   totalCost: 0,
          //   items: [],
          // },
          // discounts: {
          //   totalCost: 0,
          //   items: [],
          // },
        })) as unknown as GeneratorSystemFormData[],
      };
    }

    return {
      ...systemDesign,
      systems: systemDesign.systems,
    };
  }, [systemDesign, account]);

  const methods = useForm<GeneratorFormValues>({
    defaultValues: initialValues,
    values: initialValues,
    mode: 'onChange',
    resolver: async (values) => {
      const errors = validateGeneratorForm(values, isPreliminary);
      return { values: Object.keys(errors).length ? {} : values, errors };
    },
  });

  const {
    handleSubmit,
    formState: { isSubmitting, errors, submitCount },
    getValues,
    control,
  } = methods;

  const watchedSystems = useWatch({ control, name: 'systems' });

  const totalSystemDesignCost = calculateTotalCost({
    systems: watchedSystems as unknown as GeneratorSystem[],
  });

  const onSubmit = useCallback(
    async (data: GeneratorFormValues) => {
      try {
        setFormValues(data);
        const computedTotal = calculateTotalCost(data as unknown as { systems: GeneratorSystem[] });
        if ((activeQuote || activeContract) && computedTotal !== activeQuote?.totalSystemCost) {
          openModal();
          return;
        }
        await createSystemDesign({
          systemDesign: {
            isPreliminary,
            systems: formatSystemData(data, isPreliminary),
          },
          accountId: id,
          programType: programType as ProgramType,
        }).unwrap();
        toast.success('System design saved successfully');
        getCurrentSystemDesign({
          id,
          programType: ProgramType.generator,
        });
        await fetchPricing({
          accountId: id,
          useCurrentSystemDesign: true,
        });
        openDrawer();
      } catch (e: any) {
        toast.error(e?.data?.message || 'Error saving system design');
      }
    },
    [
      activeContract,
      activeQuote,
      calculateTotalCost,
      createSystemDesign,
      fetchPricing,
      getCurrentSystemDesign,
      id,
      isPreliminary,
      openDrawer,
      openModal,
      programType,
    ],
  );

  const handleConfirmModalSubmit = useCallback(async () => {
    closeModal();
    if (formValues) {
      try {
        if (activeContract) {
          await voidContract({
            accountId: String(id),
            contractId: activeContract.id,
          }).unwrap();
          toast.success('Active Contract voided successfully');
        }
        if (activeQuote) {
          await voidQuote({ accountId: id.toString(), quoteId: activeQuote.id.toString() }).unwrap();
          toast.success('Quote voided successfully');
        }
        await createSystemDesign({
          systemDesign: {
            isPreliminary,
            systems: formatSystemData(formValues, isPreliminary),
          },
          accountId: id,
          programType: programType as ProgramType,
        }).unwrap();
        toast.success('System design saved successfully');
        getCurrentSystemDesign({
          id,
          programType: ProgramType.generator,
        });
        await fetchPricing({
          accountId: id,
          useCurrentSystemDesign: true,
        });
        openDrawer();
      } catch (e: any) {
        toast.error(e?.data?.message || 'Error saving system design');
      }
    }
  }, [
    activeContract,
    activeQuote,
    closeModal,
    createSystemDesign,
    fetchPricing,
    formValues,
    getCurrentSystemDesign,
    id,
    isPreliminary,
    openDrawer,
    programType,
    voidContract,
    voidQuote,
  ]);

  const isVoiding = isVoidingQuote || isVoidingActiveContract;

  const ratesAndTerms = useMemo(() => {
    if (!pricingData || !pricingData.length) {
      return { rates: [], terms: [] };
    }

    const rates = Array.from(new Set(pricingData.map((item: any) => item.escalationRate)))
      .toSorted((a: any, b: any) => a - b)
      .map((rate: any) => ({
        label: `${(rate * 100).toFixed(2)}%`,
        value: rate.toString(),
        id: rate.toString(),
      }));
    const terms = Array.from(new Set(pricingData.map((item: any) => item.term)))
      .toSorted((a: any, b: any) => a - b)
      .map((term: any) => ({
        label: `${term} years`,
        value: term.toString(),
        id: term.toString(),
      }));
    return { rates, terms };
  }, [pricingData]);

  const handleCreateQuote = useCallback(
    async (values: any) => {
      const selectedProduct = pricingData.find(
        (product: any) =>
          product.term === parseInt(values.term) && product.escalationRate === parseFloat(values.rate),
      );

      if (!selectedProduct) {
        toast.error('No matching pricing found for selected term and rate');
        return;
      }

      const creationPayload = {
        quoteData: {
          productId: selectedProduct.productId,
          externalReference: account?.externalReference || undefined,
          totalFinancedAmount: systemDesignCost,
        },
        id: account.id,
        programType: ProgramType.generator,
      } as any;
      try {
        const quote = await createQuote(creationPayload).unwrap();
        const userIsAllowedToSendContract =
          comfortPlanDealerConditions && userPermissions.includes(UserPermissions.comfortPlanDealer);
        if (userIsAllowedToSendContract && quote?.status === QuoteStatus.active) {
          setCreatedQuote(quote);
          openQuoteCreationModal();
        } else {
          navigate(`/accounts/${id}`);
          toast.success('Quote created successfully');
        }
      } catch (e) {
        toast.error('Error creating quote');
        console.error(e);
      }
    },
    [
      account,
      comfortPlanDealerConditions,
      createQuote,
      id,
      navigate,
      openQuoteCreationModal,
      pricingData,
      systemDesignCost,
      userPermissions,
    ],
  );

  const handleSendContract = useCallback(async () => {
    const toastId = toast.loading('Sending contract');
    try {
      if (activeContract?.quoteId === activeQuote?.id) {
        await sendContract({
          accountId: String(activeContract?.accountId),
          contractId: String(activeContract?.id),
          forceSend: true,
        }).unwrap();
        navigate(`/accounts/${id}`);
        toast.success('Contract sent successfully');
      } else {
        toast.error('Unable to send contract: contract and active quote do not match');
      }
    } catch (e: any) {
      if (e?.data?.errors) {
        toast.error(e?.data?.errors.join(', '));
      } else {
        toast.error(e?.data?.message || 'Error sending contract');
      }
    } finally {
      toast.dismiss(toastId);
    }
  }, [activeContract, activeQuote, sendContract, navigate, id]);

  const handleCloseQuoteCreationModal = useCallback(() => {
    closeQuoteCreationModal();
    navigate(`/accounts/${id}`);
  }, [closeQuoteCreationModal, navigate, id]);

  const cancelHref = `/accounts/${id ? id : ''}`;
  const inFlightCancel = isSubmitting || isCreatingSystemDesign || isPricingLoading || isVoiding;

  return (
    <>
      <MainContainer>
        {isLoading ? (
          <Box display="block" textAlign="center" padding="lg" childGap="xl">
            <Spinner size="lg" />
          </Box>
        ) : (
          <Box
            overflow="hidden"
            childGap="lg"
            ref={targetRef}
            style={{
              marginRight: drawerMarginStyles,
              position: 'relative',
              transition: 'margin-right 0.3s ease',
            }}
          >
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                {account?.propertyDetails && (
                  <Box gap="lg">
                    <Box
                      borderColor="separator"
                      borderWidth="0 0 xs 0"
                      padding={{ desktop: 'lg 0 xl 0', base: 'lg 3xl' }}
                      direction="row"
                      childGap="5xl"
                      fontSize="md"
                      fontWeight="medium"
                    >
                      <Box>Account Details</Box>
                      <Box childGap="sm" padding={{ base: '0 0 0 5xl' }}>
                        {account?.propertyDetails?.numberOfSystems && (
                          <Box>Number of Systems: {account?.propertyDetails?.numberOfSystems}</Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}
                <SystemSection isPreliminary={isPreliminary} isPackageEditable={!hasSignedContract} />
                <Box alignItems="flex-end" margin="md" padding={{ base: 'lg 0 0 0' }} gap="xs">
                  <Box direction="row" gap="xs" alignItems="center">
                    <Box fontWeight="medium" fontSize="md">
                      Total Number of Systems:
                    </Box>
                    <Box fontWeight="bold" fontSize="lg">
                      {watchedSystems?.length ?? 0}
                    </Box>
                  </Box>
                  <Box direction="row" gap="xs" alignItems="center">
                    <Box fontWeight="medium" fontSize="md">
                      Total System Design Cost:
                    </Box>
                    <Box fontWeight="bold" fontSize="lg">
                      {currencyFormatter.format(totalSystemDesignCost)}
                    </Box>
                  </Box>
                </Box>
                {submitCount > 0 && Object.keys(errors).length > 0 && (
                  <Box padding="sm 0">
                    <Alert
                      variant="danger"
                      hasIcon
                      title="Please fix the following errors:"
                      render={() => (
                        <Box as="ul" gap="xs" fontSize="sm">
                          {flattenErrors(errors, getValues()).map((error, i) => (
                            <li key={i}>{error}</li>
                          ))}
                        </Box>
                      )}
                    />
                  </Box>
                )}
                <Box
                  direction={{
                    base: 'column',
                    tablet: 'row',
                  }}
                  childGap="sm"
                  gap="lg"
                  style={{ flexShrink: 0 }}
                  padding={{ base: 'lg' }}
                  background="primary"
                  wrap={true}
                >
                  {activeQuote ? (
                    <Box alignSelf="flex-start" direction="row" gap="lg">
                      <Box gap="xs">
                        <Box fontSize="xl" fontWeight="bold">
                          {currencyFormatter.format(activeQuote.totalSystemCost)}
                        </Box>
                        <Box fontSize="md">Total Financed Amount</Box>
                      </Box>
                      <Box gap="xs">
                        <Box direction="row" alignItems="center" gap="2xs">
                          <Box fontSize="xl" fontWeight="bold">
                            {currencyFormatter.format(
                              activeQuote.systemPricingDetails.find((d: EquipmentPricingDetails) => d.year === 1)
                                ?.monthlyPayment || 0,
                            )}
                          </Box>
                          <Box fontSize="md">/mo</Box>
                        </Box>
                        <Box fontSize="md">Year 1 Monthly Payment</Box>
                      </Box>
                      <Box gap="xs">
                        <Box direction="row" alignItems="center" gap="2xs">
                          <Box fontSize="xl" fontWeight="bold">
                            {activeQuote.systemPricingDetails.length}
                          </Box>
                          <Box fontSize="md">{activeQuote.systemPricingDetails.length > 1 ? `years` : `year`}</Box>
                        </Box>
                        <Box fontSize="md">Customer Term Length</Box>
                      </Box>
                    </Box>
                  ) : null}
                  <RequirePermissions
                    permissions={[UserPermissions.admin, UserPermissions.editor]}
                    checkAllPermissions={false}
                  >
                    <Box style={{ marginLeft: 'auto' }} direction="row" gap="sm">
                      <Button
                        as="a"
                        href={cancelHref}
                        variant="secondary"
                        tone="neutral"
                        size="md"
                        isDisabled={inFlightCancel}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="md"
                        variant="primary"
                        type="submit"
                        isDisabled={inFlightCancel}
                        isLoading={inFlightCancel}
                      >
                        Save & Get Pricing
                      </Button>
                    </Box>
                  </RequirePermissions>
                </Box>
              </form>
            </FormProvider>
            {!pricingData.length && pricingStatus !== 'uninitialized' && !isPricingLoading ? (
              <Alert variant="info" message="No pricing options available for this system design" />
            ) : null}
            <QuoteDrawer
              isOpen={isDrawerOpen}
              toggleDrawer={toggleDrawer}
              ratesAndTerms={ratesAndTerms}
              handleCreateQuote={handleCreateQuote}
              isLoading={isPricingLoading || isCreatingSystemDesign || isVoiding}
              systemDesignCost={systemDesignCost}
              isPricingLoading={isPricingLoading}
              pricingData={pricingData}
              programType={ProgramType.generator}
            />
          </Box>
        )}
      </MainContainer>

      <Modal ariaLabelledBy="costMismatch" isOpen={isModalOpen} onDismiss={closeModal}>
        <Modal.Header id="costMismatch" title="Cost Mismatch" onDismiss={closeModal} />
        <Modal.Body>
          The total system design cost does not match the active quote cost. Would you like to void the existing
          quote {activeContract ? 'and active contract' : ''} and create a new one?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" tone="neutral" onClick={closeModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmModalSubmit} disabled={!allowsAction}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        ariaLabelledBy="quoteCreationModal"
        isOpen={isQuoteCreationModalOpen}
        onDismiss={handleCloseQuoteCreationModal}
      >
        <Modal.Header
          id="quoteCreationModal"
          title="Quote Created Successfully"
          onDismiss={handleCloseQuoteCreationModal}
        />
        <Modal.Body>
          <Box gap="md" direction="column">
            <Box display="block">
              You can now send the contract to the customer for signature. To send them their contract for a{' '}
              {<strong>{createdQuote?.systemPricingDetails?.length}</strong>}{' '}
              {(createdQuote?.systemPricingDetails?.length ?? 0) > 1 ? 'years' : 'year'} Comfort Plan with an Initial
              Monthly Payment of{' '}
              {
                <strong>
                  {currencyFormatter.format(
                    createdQuote?.systemPricingDetails?.find((d: EquipmentPricingDetails) => d.year === 1)
                      ?.monthlyPayment ?? 0,
                  )}
                </strong>
              }
              , click the button below.
            </Box>
            <Box display="block">
              The customer will also be invited to create their Palmetto Account where they must complete all the
              Notice to Proceed steps. The customer must complete all steps in their account in order to achieve NTP.
              Our chat support team is standing by to assist you!
            </Box>
          </Box>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleSendContract} disabled={isSendingContract}>
            Send Contract
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
